import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReportsTable } from '../ReportGroup';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { Program } from '../../Reports/NewReportDialog';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modalTitleStyles,
} from 'shared/design-system/components/TrueImpactModal';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { changeReportProgram } from './api';
import { AllReportYears } from '../api';
import { COLORS } from 'shared/design-system/settings/variables/Colors';

interface ChangeProgramModalProps {
  report: ReportsTable;
  reportsState: RequestState<AllReportYears>;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  handleClose: () => unknown;
  handleReloadReportPage: () => unknown;
}

const StyledDiv = styled.div`
  padding: ${SPACERS.large};
  .dropdown-wrapper {
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.tiny};
  }
`;

const ChangeProgramModal: React.FC<ChangeProgramModalProps> = ({
  report,
  reportsState,
  programsList,
  handleClose,
  handleReloadReportPage,
}) => {
  const client = useClient();
  const [selectedProgram, setSelectedProgram] = useState<Program | undefined>(
    undefined,
  );
  const [changeProgramRequest, makeChangeProgramRequest] = useGraphQLRequest<{
    updateReport: { success: boolean };
  }>();

  const handleDone = useCallback(() => {
    handleReloadReportPage();
    handleClose();
  }, [handleReloadReportPage, handleClose]);

  const handleChangeProgram = useCallback(() => {
    if (!client || !selectedProgram) return;
    makeChangeProgramRequest(
      changeReportProgram(client, report.dbId, selectedProgram.Id),
    );
  }, [makeChangeProgramRequest, selectedProgram]);

  const programOptions = programsList.data?.allPrograms || [];
  const isLoading = changeProgramRequest.type === 'REQUEST_START';
  const changeProgramSuccess =
    selectedProgram &&
    changeProgramRequest.type === 'REQUEST_SUCCESS' &&
    changeProgramRequest.data.updateReport.success;

  const changeProgramError = `A report for ${report.year} is already assigned to the selected program. Please choose another program.`;

  return (
    <TrueImpactModal
      open={true}
      maxWidth="700px"
      onClose={handleClose}
      errorText={
        changeProgramRequest.type === 'REQUEST_ERROR'
          ? changeProgramError
          : undefined
      }
      title="Change Program"
      addStyles={modalHeightAutoStyles + modalTitleStyles}
      next={{
        onClick: changeProgramSuccess ? handleDone : handleChangeProgram,
        text: changeProgramSuccess ? 'Refresh' : 'Change program',
        disabled: isLoading || !selectedProgram,
        isLoading,
      }}
      previous={undefined}
    >
      <StyledDiv>
        <TrueImpactText>
          Are you sure you want to change the program of this report?
          <br />
          <strong>
            {report.program.name} ({report.year})
          </strong>
        </TrueImpactText>
        <div className="dropdown-wrapper">
          <TrueImpactDropdown
            value={selectedProgram}
            options={programOptions}
            renderValue={p => p.name || 'Please select the new program'}
            getId={p => p.Id}
            getLabel={p => p.name}
            native={true}
            disabled={changeProgramSuccess}
            onChange={p => setSelectedProgram(p)}
            onClear={undefined}
            getItemDisability={p =>
              Boolean(
                reportsState.data?.[p.Id]?.byYears?.[
                  typeof report.year === 'string'
                    ? Number(report.year)
                    : report.year
                ],
              ) || p.Id === report.program.Id
            }
            name="Please select the new program"
          />
        </div>
        {changeProgramSuccess && (
          <TrueImpactText color="success">
            Successfully changed program to{' '}
            <strong>{selectedProgram.name}</strong>.
          </TrueImpactText>
        )}
      </StyledDiv>
    </TrueImpactModal>
  );
};

export default ChangeProgramModal;
